import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { api } from '@/lib/db';
import { Card, Badge } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const data = await api.getSuperAdminStats();
      setStats(data);
    };
    fetchStats();
  }, []);

  if (!stats) return (
    <div className="flex items-center justify-center p-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
    </div>
  );

  const statusChartOptions = {
    tooltip: { trigger: 'item' },
    series: [{
      type: 'pie',
      radius: ['60%', '85%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      data: [
        { value: stats.activeSubscriptions, name: 'Aktív', itemStyle: { color: '#10b981' } },
        { value: stats.expiredSubscriptions, name: 'Lejárt', itemStyle: { color: '#ef4444' } }
      ]
    }]
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Rendszer Állapot</h1>
        <p className="text-gray-500">Vállalkozások előfizetéseinek és aktivitásának nyomon követése.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Összes Ügyfél</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-gray-900">{stats.totalBusinesses}</h3>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><SafeIcon name="Briefcase" /></div>
          </div>
        </Card>

        <Card className="p-6 border-b-4 border-green-500">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Aktív Előfizetés</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-green-600">{stats.activeSubscriptions}</h3>
            <div className="p-2 bg-green-50 text-green-600 rounded-lg"><SafeIcon name="CheckCircle" /></div>
          </div>
        </Card>

        <Card className="p-6 border-b-4 border-red-500">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Lejárt Hozzáférés</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-red-600">{stats.expiredSubscriptions}</h3>
            <div className="p-2 bg-red-50 text-red-600 rounded-lg"><SafeIcon name="AlertCircle" /></div>
          </div>
        </Card>

        <Card className="p-6">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Összes Értékelés</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-gray-900">{stats.totalReviews}</h3>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><SafeIcon name="MessageSquare" /></div>
          </div>
        </Card>

        <Card className="p-6 border-b-4 border-blue-500">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Google Átirányítás</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-blue-600">{stats.totalRedirects}</h3>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><SafeIcon name="ExternalLink" /></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">4–5 csillagos elégedett vendégek</p>
        </Card>

        <Card className="p-6 border-b-4 border-orange-500">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Visszajelzés beérkezett</p>
          <div className="flex justify-between items-end mt-2">
            <h3 className="text-3xl font-black text-orange-600">{stats.totalCaptured}</h3>
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><SafeIcon name="ShieldCheck" /></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">1–3 csillagos kezelt panasz</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <SafeIcon name="TrendingUp" className="text-blue-600" /> Ügyfél Aktivitás Rangsor
          </h3>
          <div className="space-y-4">
            {stats.businessActivity.slice(0, 5).map((biz) => (
              <div key={biz.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${biz.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="font-bold text-gray-900">{biz.name}</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Aktivitás</p>
                    <p className="font-black text-blue-600">{biz.activity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Lejárat</p>
                    <p className="text-xs text-gray-600">{biz.expiry_date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Előfizetési Arány</h3>
          <p className="text-sm text-gray-500 mb-8">Aktív vs. Lejárt fiókok megoszlása.</p>
          <div className="h-[250px]">
            <ReactECharts option={statusChartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
          <div className="mt-4 space-y-2">
             <div className="flex justify-between text-sm">
                <span className="text-gray-500">Aktív:</span>
                <span className="font-bold text-green-600">{stats.activeSubscriptions}</span>
             </div>
             <div className="flex justify-between text-sm">
                <span className="text-gray-500">Lejárt:</span>
                <span className="font-bold text-red-600">{stats.expiredSubscriptions}</span>
             </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;